import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { TemplateProvider } from './context/TemplateContext';
import { AssetProvider } from './context/AssetContext';
import { RenderProvider } from './context/RenderContext';
import { App } from './App';
import { ModeSelector, type AppMode } from './ModeSelector';
import { ShowBuilderApp } from './show/ShowBuilderApp';
import './index.css';

// ─── Root — mode switcher ─────────────────────────────────────

const STORAGE_KEY = 'buzz24_last_mode';

function AppRoot() {
  const [mode, setMode] = useState<AppMode | null>(() => {
    // Remember last mode across refreshes (optional UX convenience)
    const saved = localStorage.getItem(STORAGE_KEY) as AppMode | null;
    return saved ?? null;
  });

  const handleSelect = (selected: AppMode) => {
    localStorage.setItem(STORAGE_KEY, selected);
    setMode(selected);
  };

  const handleBack = () => {
    localStorage.removeItem(STORAGE_KEY);
    setMode(null);
  };

  if (mode === null) {
    return <ModeSelector onSelect={handleSelect} />;
  }

  if (mode === 'single') {
    return (
      <TemplateProvider>
        <AssetProvider>
          <RenderProvider>
            {/* Back button overlaid on top-left */}
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <App />
              <button
                onClick={handleBack}
                title="Back to Mode Selector"
                style={{
                  position: 'fixed', top: 8, left: 8, zIndex: 9999,
                  background: 'rgba(15,23,42,0.85)',
                  border: '1px solid #334155',
                  borderRadius: 6, padding: '4px 10px',
                  cursor: 'pointer', color: '#94a3b8', fontSize: 10,
                  display: 'flex', alignItems: 'center', gap: 4,
                  backdropFilter: 'blur(4px)',
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M6 2L2 5l4 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Home
              </button>
            </div>
          </RenderProvider>
        </AssetProvider>
      </TemplateProvider>
    );
  }

  if (mode === 'multi') {
    return <ShowBuilderApp onBack={handleBack} />;
  }

  return null;
}

// ─── Mount ────────────────────────────────────────────────────

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found in index.html');

createRoot(rootElement).render(
  <React.StrictMode>
    <AppRoot />
  </React.StrictMode>
);
